import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  CalendarDaysIcon,
  MapPinIcon,
  PackageIcon,
  StoreIcon,
  TruckIcon,
} from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ClientStatusChip } from '@/shared/ui/chips/ClientStatusChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { useUserPackageDetail } from '@/entities/user-packages';
import { formatDate } from '@/shared/utils/date';
import { formatUsd } from '@/shared/utils/currency';
import { cn } from '@/shared/utils/cn';
import type { ApiUserPackageDetailParcel } from '@/shared/types/api/packages';

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
    <span className="text-ink-muted">{label}</span>
    <span className="text-right font-medium text-ink">{value || '—'}</span>
  </div>
);

// Card body — identical visual shape for clickable and non-clickable rows so
// the FE only swaps the wrapper, not the contents (matches the spec).
const ParcelCardBody = ({ parcel }: { parcel: ApiUserPackageDetailParcel }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
            <PackageIcon className="h-4 w-4" aria-hidden />
          </span>
          <span className="truncate text-sm font-semibold text-ink">{parcel.name}</span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <ClientStatusChip status={parcel.client_status ?? 'IN_PROGRESS'} />
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-1.5 text-xs text-ink-muted sm:grid-cols-2">
        <div className="flex items-center gap-1.5">
          <TruckIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">{t('parcels:trek', { trekId: parcel.trek_id })}</span>
        </div>
        {parcel.store_name && (
          <div className="flex items-center gap-1.5">
            <StoreIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{parcel.store_name}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">{t(`regions:${parcel.region}`)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{formatDate(parcel.created_at)}</span>
        </div>
      </dl>

      <div className="flex items-end justify-between border-t border-slate-100 pt-2 text-sm">
        <span className="text-ink-muted">{t('common:total')}</span>
        <span className="font-semibold text-ink">{formatUsd(parcel.total_amount)}</span>
      </div>
    </>
  );
};

// Each parcel row: clickable Link when it belongs to the caller, plain
// muted container otherwise. Owner spec: non-owned rows render the same
// visuals so the user can see the carton context, but cannot drill in.
const PackageParcelRow = ({ parcel }: { parcel: ApiUserPackageDetailParcel }) => {
  const { t } = useTranslation();

  if (parcel.is_mine) {
    return (
      <Link
        to={`/parcels/${parcel.id}`}
        className="group flex cursor-pointer flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card transition-colors hover:border-brand-200 hover:bg-brand-50/40 active:bg-brand-50/60"
      >
        <ParcelCardBody parcel={parcel} />
      </Link>
    );
  }

  const notMineLabel = t('packages:notMineLabel');
  return (
    <div
      aria-label={notMineLabel}
      title={notMineLabel}
      className={cn(
        'flex cursor-not-allowed flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card opacity-70',
      )}
    >
      <ParcelCardBody parcel={parcel} />
    </div>
  );
};

export const WidgetsPackageDetailMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const query = useUserPackageDetail(id);

  if (query.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <>
        <PageHeader
          title={t('packages:detailTitle')}
          showBack
          backTo="/packages"
        />
        <div className="p-4">
          <ErrorState
            message={extractErrorMessage(query.error, t('packages:detailNotFound'))}
            onRetry={() => void query.refetch()}
          />
        </div>
      </>
    );
  }

  const pkg = query.data;
  const priceNumber = Number.parseFloat(pkg.price || '0');
  const weightText = pkg.weight
    ? `${pkg.weight} ${pkg.weight_unit}`
    : '—';

  return (
    <>
      <PageHeader title={pkg.name} showBack backTo="/packages" />
      <div className="flex flex-col gap-3 p-4 lg:gap-4 lg:p-6">
        <Section title={t('packages:sections.info')}>
          <Row label={t('packages:fields.name')} value={pkg.name} />
          <Row label={t('packages:fields.price')} value={formatUsd(priceNumber)} />
          <Row label={t('packages:fields.weight')} value={weightText} />
          <Row
            label={t('packages:fields.createdAt')}
            value={formatDate(pkg.created_at)}
          />
        </Section>

        <Section
          title={t('packages:sections.parcels', { count: pkg.parcels.length })}
        >
          {pkg.parcels.length === 0 ? (
            <EmptyState
              icon={<PackageIcon className="h-10 w-10" />}
              title={t('packages:noParcels')}
            />
          ) : (
            <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
              {pkg.parcels.map((parcel) => (
                <li key={parcel.id}>
                  <PackageParcelRow parcel={parcel} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </>
  );
};
